/**
 * CurrencyInput Component
 *
 * MUI TextField wrapper that formats input as Brazilian currency (R$).
 *
 * Stores value internally as a number in REAIS (e.g., 1234.56) — matching the
 * existing form contract used by EPC/EPI wizards. Displays formatted value
 * (e.g., "R$ 1.234,56") to the user.
 *
 * Uses the "calculator" pattern: each digit typed shifts the value.
 *   "5"      → R$ 0,05  (value: 0.05)
 *   "500000" → R$ 5.000,00 (value: 5000.00)
 *
 * @version 2025.1.0
 */

import React from 'react';
import { TextField, InputAdornment } from '@mui/material';

interface CurrencyInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  name?: string;
  onBlur?: () => void;
}

/**
 * Format a number (in reais) as Brazilian currency display string.
 * 1234.56 → "1.234,56"
 */
function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Parse a display string back into a number (in reais).
 * Strips all non-digits and divides by 100 to compute decimal value.
 * "" → undefined
 * "5" → 0.05
 * "500000" → 5000.00
 */
function parseBRL(input: string): number | undefined {
  const digits = input.replace(/\D/g, '');
  if (digits === '') return undefined;
  const cents = parseInt(digits, 10);
  if (Number.isNaN(cents)) return undefined;
  return cents / 100;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  label,
  helperText,
  error,
  disabled,
  fullWidth,
  required,
  name,
  onBlur,
}) => {
  const displayValue = value === undefined || value === null ? '' : formatBRL(value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parsed = parseBRL(event.target.value);
    onChange(parsed);
  };

  return (
    <TextField
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={onBlur}
      name={name}
      fullWidth={fullWidth}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          inputMode: 'numeric',
        },
      }}
    />
  );
};

export default CurrencyInput;
